<template>
  <section>
    <RevButton
      v-if="shouldShowClearButton"
      class="mb-7"
      :icon="IconCross"
      size="small"
      variant="primary"
      @click="handleClearAllFacets"
    >
      {{ i18n(translations.clearAll) }}
    </RevButton>
    <RevAccordionList>
      <RevAccordionItem v-for="(facet, facetName) in facets" :key="facetName">
        <template #title>
          <h3 class="body-1-bold capitalize">
            {{ facetName }}
          </h3>
        </template>

        <template
          #description
          v-if="shouldShowDescriptionForFacetName(facetName)"
        >
          {{ getStringifiedValueForFacetName(facetName) }}
        </template>

        <template #body>
          <Facet
            :facet
            :model-value="getValueForFacetName(facetName)"
            :name="facetName"
            @update:model-value="handleFacetValueChange"
          />
        </template>
      </RevAccordionItem>
    </RevAccordionList>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevAccordionList } from '@ds/components/AccordionList'
import { RevButton } from '@ds/components/Button'
import { IconCross } from '@ds/icons/IconCross'

import { FACET_NAME_PREFIX } from './Facets.constants'
import translations from './Facets.translations'
import Facet from './components/Facet/Facet.vue'

const props = defineProps<{
  facets: Record<string, Record<string, string>>
  modelValue: Record<string, string>
}>()

const emit = defineEmits(['update:modelValue'])

const i18n = useI18n()
const shouldShowClearButton = computed(() => !isEmpty(props.modelValue))

function getValueForFacetName(facetName: string) {
  return props.modelValue[`${FACET_NAME_PREFIX}${facetName}`]
}

function getStringifiedValueForFacetName(facetName: string) {
  const value = getValueForFacetName(facetName)

  return Array.isArray(value) ? value.join(', ') : value
}

function shouldShowDescriptionForFacetName(facetName: string) {
  const value = getValueForFacetName(facetName)

  return !isEmpty(value)
}

function handleClearAllFacets() {
  emit('update:modelValue', {})
}

function handleFacetValueChange({
  name,
  value,
}: {
  name: string
  value: null | string
}) {
  const key = `${FACET_NAME_PREFIX}${name}`
  const newFacetsValues = { ...props.modelValue }
  if (value) {
    newFacetsValues[key] = value
  } else {
    delete newFacetsValues[key]
  }

  emit('update:modelValue', newFacetsValues)
}
</script>
